import toUpper from "lodash/toUpper"
import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import identity from "lodash/identity"
import filter from "lodash/filter"
import get from "lodash/get"
import split from "lodash/split"
import noop from "lodash/noop"
import { AuthoringUtils } from "@adobe/aem-spa-page-model-manager"
import CircularProgress from "component-lib/Base/components/CircularProgress"
import { Box } from "component-lib/Base/components/Box"
import Typography from "component-lib/Base/components/Typography"
import { makeStyles } from "tss-react/mui"
import { ButtonPrimary, FormattedTextField } from "component-lib/Base"
import { API_STATUSES, LANG_OPTIONS_MAP } from "constants-lib/common"
import { CONTACT_METHODS } from "constants-lib/contact"
import { useState, useEffect, useRef } from "react"
import { ReactSVG } from "react-svg"
import URI from "urijs"
import URITemplate from "urijs/src/URITemplate"
import { urls } from "utils-lib/builds"
import getContentUrl from "utils-lib/getContentUrl"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import isSupportPage from "utils-lib/isSupportPage"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import {
  formatPhoneNumberWithDashes,
  getFormattedCustomerSupportPhoneNumber,
} from "utils-lib/strings"
import { withLocalization } from "hoc-lib/localization"
import { getWindow } from "utils-lib/getWindow"
import { fakeAnalyticsClick, updateClicksDL } from "utils-lib/analytics"
import {
  pushToAdobeDataLayer,
  getPagename,
} from "utils-lib/analytics/adobeDataLayer"

const ssrWindow = getWindow()

const FALLBACK_SUPPORT_NUMBER_US = `8007969696`
const FALLBACK_SUPPORT_NUMBER_CA = `8006651898`

// maps to topic/subtopic/action
const SUPPORT_FORM_TOPIC_MAPPING = {
  "BILLING_&_PAYMENTS_INVOICE_QUESTION_EXPLANATION_OF_COMMON_CHARGES": `/support/billing-questions/billing-question-form`,
  "BILLING_&_PAYMENTS_REQUEST_AN_INVOICE_VIEW_INVOICE_HISTORY_AND_PENDING_CHARGES": `/support/request-an-invoice/request-an-invoice-form`,
  SERVICE_QUESTIONS_CONTAINER_DELIVERY_STATUS_CONTACT_US: `/support/container-delivery-status/request-container-delivery-status-form`,
  SERVICE_QUESTIONS_CONTAINER_DELIVERY_STATUS: `/support/container-delivery-status/request-container-delivery-status-form`,
  SERVICE_QUESTIONS_SUSPEND_OR_RESUME_SERVICE_CONTACT_US: `/support/suspend-or-resume-service/suspend-or-resume-service-form`,
  SERVICE_QUESTIONS_SUSPEND_OR_RESUME_SERVICE: `/support/suspend-or-resume-service/suspend-or-resume-service-form`,
  MANAGE_MY_ACCOUNT_UPDATE_BILLING_ADDRESS_CONTACT_US: `/support/update-billing-address/update-billing-address-form`,
  MANAGE_MY_ACCOUNT_UPDATE_BILLING_ADDRESS: `/support/update-billing-address/update-billing-address-form`,
}

// maps to articles on the support articles page
const SUPPORT_FORM_ARTICLE_MAPPING = {
  "/support/faqs/what-are-the-charges-on-my-waste-management-invoice": `/support/billing-questions/billing-question-form`,
  "/support/faqs/where-do-i-find-payment-billing-history-or-pending-charges": `/support/request-an-invoice/request-an-invoice-form`,
}

const CONTACT_BACK_AVAILABLE_OPTIONS = "contact-back-available-options"
const BULK_PICKUP_EXC_GUEST = `BulkPickup_ExceptionPopup_Guest`
const REQUEST_HELP_CONTACT_BACK = "requestHelpContactBack"
const RIGHT_CHANNELING_OPTIONS = "rightChannelingOptions"
const RIGHT_CHANNELING_CHAT = "rightChannelingChat"
const RIGHT_CHANNELING_CALL = "rightChannelingCall"
const RIGHT_CHANNELING_CONTACT_BACK = "rightChannelingContactBack"
const RIGHT_CHANNELING_EMAIL = "rightChannelingEmail"

export const strings = {
  en_CA: {
    ERROR_MESSAGE: `Oops! Something went wrong. Please try again.`,
  },
  fr_CA: {
    ERROR_MESSAGE: `Oups! Il y a eu un problème. Veuillez réessayer.`,
  },
}

const useStyles = makeStyles()((theme) => ({
  header: {
    fontSize: `1.5rem`,
    marginBottom: `1rem`,
    color: theme.palette.primary.black,
    [theme.breakpoints.down("sm")]: {
      fontSize: `1.25rem`,
    },
  },
  backButton: {
    marginTop: `1rem`,
  },
  contentContainer: ({ showContactBackConfirmation }) => ({
    marginBottom: `1rem`,
    textAlign: `left`,
    ...(showContactBackConfirmation
      ? {
          display: `flex`,
          flexDirection: `column`,
          justifyContent: `space-around`,
          alignItems: `flex-start`,
        }
      : {}),
  }),
  tiles: {
    marginTop: `1rem`,
    display: `flex`,
    gap: `1.5rem`,
    justifyContent: `flex-start`,
    flexWrap: `wrap`,
  },
  card: {
    display: `flex`,
    flexDirection: `column`,
    minHeight: `15.5rem`,
    width: `18.5rem`,
    padding: `1rem 1.5rem 0 1.5rem`,
    borderRadius: `8px`,
    boxShadow: `0 2px 8px 0 ${theme.palette.background.dropShadow}`,
    justifyContent: `space-between`,
    [theme.breakpoints.down("sm")]: {
      width: `100%`,
      height: `fit-content`,
    },
  },
  cardTopHalf: {
    display: `flex`,
    flexDirection: `column`,
    justifyContent: `flex-start`,
    alignItems: `center`,
    textAlign: `center`,
    marginBottom: `1rem`,
  },
  tileHeading: {
    color: theme.palette.common.black,
    fontSize: `1rem !important`,
  },
  tileBody: {
    fontSize: `0.875rem`,
  },
  tileButton: {
    marginBottom: `1rem`,
    alignSelf: `stretch`,
    justifySelf: `flex-end`,
  },
  phoneNumber: {
    cursor: `initial`,
    "& a": {
      color: theme.palette.primary.main,
    },
  },
  messageContainer: {
    padding: `1rem`,
    borderRadius: 3,
    backgroundColor: theme.palette.background.exceptionsYellow,
  },
  tileIcon: {
    display: `flex`,
    marginRight: `0.25rem`,
    "& svg": {
      height: `2.875rem !important`,
    },
    "& svg *": {
      fill: theme.palette.text.lightHeader,
    },
  },
  informationIcon: {
    display: `flex`,
    marginRight: `0.25rem`,
    "& svg": {
      width: `1.5rem`,
      height: `1.5rem`,
    },
    "& svg *": {
      fill: theme.palette.text.lightHeader,
    },
  },
}))

export const SupportOptionsCards = ({
  strings,
  contactOptions,
  unavailableServiceHeader = "",
  unavailableServiceText = "",
  renderParentHeader = () => {},
  topSupportTopicData,
  customerSelections,
  customerSupportContact,
  language,
  // eslint-disable-next-line no-unused-vars
  isLoggedIn,
  getLiveChat,
  contactBackUrl,
  confirmationIcon = "",
  redirectMessage = "",
  hidePhoneForBusinessLOB,
  hidePhoneForHomeLOB,
  handleCloseModal,
  hasError,
  setHasError,
  cityBilledAnalytics = false,
  cityBilledAnalyticsData = {},
  isLobHome,
  isLobBusiness,
  // when not utilizing support article mapping and email form
  bypassSupportServlet = false,
  setCustomerLob = noop,
  pageLob = "",
}) => {
  const [showContactBackConfirmation, setShowContactBackConfirmation] =
    useState(false)
  const [analyticsFlag, setAnalyticsFlag] = useState(true)
  const [isPhoneAllowedByAuthoring, setIsPhoneAllowedByAuthoring] =
    useState(true)
  const [customerSupportNumber, setCustomerSupportNumber] = useState(``)

  const timeoutRef = useRef()
  const pushData = useRef(false)

  const isInEditor = AuthoringUtils.isInEditor()

  const { LDFFReleaseDataLayerCancelServicePhoneNumberPopulationFix = false } =
    useFeatureFlags([
      "LDFFReleaseDataLayerCancelServicePhoneNumberPopulationFix",
    ])

  const { classes } = useStyles({ isInEditor, showContactBackConfirmation })
  const {
    customerAddress,
    contactBackAttributesData,
    contactBackAttributesStatus,
  } = customerSelections
  const { phone, liveChat } = customerSupportContact

  const customerContactGuidance =
    contactBackAttributesData?.customer_contact_guidance
  const isContactBackEligible =
    contactBackAttributesData?.is_contact_back_enabled
  const isContactBackLiveChatEnabled =
    contactBackAttributesData?.is_livechat_enabled
  const isDuringHours = get(liveChat, `chatStatus`, `0`) === `1`
  const currentPageIsSupportPage = isSupportPage()

  const renderTypeGuidance = !isEmpty(customerContactGuidance)

  const renderTypeChat = isContactBackLiveChatEnabled ?? true // Render chat by default if it's undefined or null

  const renderTypeContactBack =
    isContactBackEligible &&
    !renderTypeGuidance &&
    ((isContactBackLiveChatEnabled && !isDuringHours) ||
      !isContactBackLiveChatEnabled)
  const renderTypePhone = !renderTypeContactBack

  const isChatAllThatsLeft = !renderTypeContactBack && !renderTypePhone
  const renderTypeFallback =
    !renderTypeContactBack &&
    !renderTypeGuidance &&
    !isChatAllThatsLeft &&
    (!renderTypeChat || (renderTypeChat && !isDuringHours))

  useEffect(() => {
    setIsPhoneAllowedByAuthoring(
      !(isLobHome && hidePhoneForHomeLOB) &&
        !(isLobBusiness && hidePhoneForBusinessLOB),
    )
  }, [isLobHome, isLobBusiness])

  const getFormattedCustomerSupportNumber = () => {
    const isUS = language === LANG_OPTIONS_MAP.EN_US
    const fallback = isUS
      ? FALLBACK_SUPPORT_NUMBER_US
      : FALLBACK_SUPPORT_NUMBER_CA

    const contactPref = get(phone, `contactPref`, [])
    const contactType = `CustomerService`
    const contactNumber = contactPref?.find(
      (contact) => contact.contactType === contactType,
    )
    const formattedNumber = formatPhoneNumberWithDashes(
      get(contactNumber, `phone`, fallback),
    )

    return formattedNumber
  }
  const setAnalyticsKey = (key) => {
    const analyticsMap = {
      call: {
        cityBilled: "city-billed-bulk-pickup-request-help-options-call-now",
        rightChanneling: "rightChannelingCall",
        default: "right-channeling-request-help-options-call-now",
      },
      chat: {
        cityBilled: "city-billed-bulk-pickup-request-help-options-chat-now",
        rightChanneling: "rightChannelingChat",
        default: "right-channeling-request-help-options-chat-now",
      },
      email: {
        cityBilled: "city-billed-bulk-pickup-request-help-options-email-now",
        rightChanneling: "rightChannelingEmail",
        default: "right-channeling-request-help-options-email-now",
      },
      contactBack: {
        cityBilled:
          "city-billed-bulk-pickup-request-help-options-start-request",
        rightChanneling: "rightChannelingContactBack",
        default: "right-channeling-request-help-options-start-contact-back",
      },
    }

    const keyConfig = analyticsMap[key]
    if (!keyConfig) return ""

    if (cityBilledAnalytics) {
      return keyConfig.cityBilled
    }

    if (!cityBilledAnalytics) {
      return keyConfig.rightChanneling
    }

    return keyConfig.default
  }

  const pushDataWithPageDetails = (eventName) => {
    const rightChannelAnalyticsData = {
      eventInfo: {
        eventName,
      },
      clicks: {
        page: {
          pageURL: ssrWindow.location.pathname,
          pageName: getPagename(language),
        },
      },
    }
    pushToAdobeDataLayer(eventName, rightChannelAnalyticsData)
  }

  useEffect(() => {
    setCustomerSupportNumber(getFormattedCustomerSupportNumber())
  }, [customerSupportContact?.phone, language])

  useEffect(() => {
    const areAllConditionsMet =
      (contactBackAttributesStatus === API_STATUSES.FULFILLED ||
        contactBackAttributesStatus === API_STATUSES.FAILED) &&
      (liveChat?.fetchState === API_STATUSES.FULFILLED ||
        liveChat?.fetchState === API_STATUSES.FAILED) &&
      (phone?.fetchState === API_STATUSES.FULFILLED ||
        phone?.fetchState === "" ||
        phone?.fetchState === API_STATUSES.FAILED)

    if (areAllConditionsMet && !pushData.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        let callAvailable = false
        let CallPhoneNumber = ""
        let liveChatAvailable = false
        let contactBackRequest = true

        const additionalContent = renderPhoneNumber()
        if (
          ((renderTypePhone && isPhoneAllowedByAuthoring) ||
            renderTypeGuidance) &&
          additionalContent &&
          !renderTypeFallback
        ) {
          const guidancePhoneNumber = customerContactGuidance?.match(
            /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/,
          )?.[0]

          CallPhoneNumber = renderTypeGuidance
            ? LDFFReleaseDataLayerCancelServicePhoneNumberPopulationFix
              ? guidancePhoneNumber || getFormattedCustomerSupportNumber()
              : getFormattedCustomerSupportPhoneNumber(customerContactGuidance)
            : getFormattedCustomerSupportNumber()
          callAvailable = true
        }
        if (
          (isInEditor || renderTypeChat || isChatAllThatsLeft) &&
          isDuringHours
        ) {
          liveChatAvailable = true
        }
        if (!isInEditor && !renderTypeContactBack) {
          contactBackRequest = false
        }
        const chat_Mon_Fri_Hours = liveChat?.MonFriHrs
          ? liveChat?.MonFriHrs?.split(`US`)[0]
          : ""
        const chat_Sat_Hours = liveChat?.SatHrs
          ? liveChat?.SatHrs?.split(`US`)[0]
          : ""

        const analyticsData = {
          clicks: {
            eventInfo: {
              eventName: RIGHT_CHANNELING_OPTIONS,
            },
            object_content: {
              serviceAddress: customerAddress?.formattedAddress,
              serviceZipCode: customerAddress?.postalCode,
              lineOfBusiness: customerSelections?.customerLob || "",
              callAvailable,
              callPhoneNumber: CallPhoneNumber,
              liveChatAvailable,
              chatMonFriHours: liveChatAvailable ? chat_Mon_Fri_Hours : "",
              chatSatHours: liveChatAvailable ? chat_Sat_Hours : "",
              contactBackRequestAvailable: contactBackRequest,
            },
          },
          page: {
            pageURL: ssrWindow.location.pathname,
            pageName: getPagename(language),
          },
        }
        pushToAdobeDataLayer(RIGHT_CHANNELING_OPTIONS, analyticsData)
        pushData.current = true
      }, 100)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    contactBackAttributesStatus,
    liveChat?.fetchState,
    phone?.fetchState,
    isLobHome,
    hidePhoneForHomeLOB,
    isLobBusiness,
    hidePhoneForBusinessLOB,
    isPhoneAllowedByAuthoring,
    renderTypePhone,
    renderTypeGuidance,
    renderTypeFallback,
    renderTypeContactBack,
    renderTypeChat,
    isChatAllThatsLeft,
    isDuringHours,
    isInEditor,
    customerContactGuidance,
    LDFFReleaseDataLayerCancelServicePhoneNumberPopulationFix,
  ])

  useEffect(() => {
    let additionalContent = ""
    let liveChatAvailable = false
    let callAvailable = false
    let contactBackRequest = true
    let CallPhoneNumber = ""

    additionalContent = renderPhoneNumber()

    if (liveChat?.chatStatus !== undefined) {
      if (
        ((renderTypePhone && isPhoneAllowedByAuthoring) ||
          renderTypeGuidance) &&
        additionalContent
      ) {
        CallPhoneNumber = renderTypeGuidance
          ? getFormattedCustomerSupportPhoneNumber(customerContactGuidance)
          : getFormattedCustomerSupportNumber()
        callAvailable = true
      }

      if (
        (isInEditor || renderTypeChat || isChatAllThatsLeft) &&
        isDuringHours
      ) {
        liveChatAvailable = true
      }

      if (!isInEditor && !renderTypeContactBack) {
        contactBackRequest = false
      }

      const analyticsData = {
        is_livechat_enabled: liveChatAvailable,
        chat_Mon_Fri_Hours: liveChat?.MonFriHrs
          ? liveChat?.MonFriHrs?.split(`US`)[0]
          : "",
        chat_Sat_Hours: liveChat?.SatHrs
          ? liveChat?.SatHrs?.split(`US`)[0]
          : "",
        is_call_enabled: callAvailable,
        call_Phone_Number: CallPhoneNumber,
        is_contact_back_enabled: contactBackRequest,
        ...(cityBilledAnalytics && cityBilledAnalyticsData),
      }
      if (
        (hasError && analyticsFlag) ||
        (cityBilledAnalytics && analyticsFlag)
      ) {
        if (cityBilledAnalytics) {
          const {
            serviceAddress,
            reason,
            lob,
            isEligible,
            miscInstructions,
            scenarioName,
            marketArea,
            gisId,
            addressComponentLocation,
            is_livechat_enabled,
            is_call_enabled,
            is_contact_back_enabled,
            call_Phone_Number,
            chat_Mon_Fri_Hours,
            chat_Sat_Hours,
          } = analyticsData || {}

          const addressPart = split(serviceAddress, ",")[2]
          const zipCode = split(addressPart, " ")[2]

          const bulkPickupExcGuestData = {
            clicks: {
              object_content: {
                serviceAddress,
                zipCode,
                reason,
                lob,
                isEligible,
                miscInstructions,
                scenarioName,
                marketArea,
                gisIds: gisId,
                addressComponentLocation,
                isLiveChatEnabled: is_livechat_enabled ?? "",
                isCallEnabled: is_call_enabled ?? "",
                isContactBackEnabled: is_contact_back_enabled ?? "",
                callPhoneNumber: call_Phone_Number ?? "",
                chatMonFriHours: chat_Mon_Fri_Hours ?? "",
                chatSatHours: chat_Sat_Hours ?? "",
              },
            },
          }

          pushToAdobeDataLayer(BULK_PICKUP_EXC_GUEST, bulkPickupExcGuestData)
        } else {
          updateClicksDL({
            ui_element: CONTACT_BACK_AVAILABLE_OPTIONS,
            object_content: analyticsData,
          })
          fakeAnalyticsClick({
            dummyClass: CONTACT_BACK_AVAILABLE_OPTIONS,
            analytics_key: `${CONTACT_BACK_AVAILABLE_OPTIONS}-${JSON.stringify(
              analyticsData,
            )}`,
          })
        }
        setAnalyticsFlag(false)
      }
    }
  }, [
    hasError,
    cityBilledAnalytics,
    liveChat,
    customerSupportContact?.phone,
    language,
  ])

  const handleChatClick = async () => {
    pushDataWithPageDetails(RIGHT_CHANNELING_CHAT)
    setHasError(false)

    let liveChatData = { ...liveChat }

    const postalCode = get(customerAddress, "postalCode", "")
    if (hasError && postalCode) {
      const data = await getLiveChat(postalCode)
      liveChatData = data.payload
    }

    const popupHeight = 900

    const url =
      liveChatData.fetchState === API_STATUSES.FULFILLED &&
      liveChatData.gensysChatUrl !== undefined
        ? liveChatData.gensysChatUrl
        : ``

    if (url) {
      const { segment, genesysMA, requestTrackingId } = liveChatData

      const api = `GENESYS_CHAT_POPUP`
      const template = URITemplate(urls.url[api])
      const urlWithParams = template.expand({
        url,
        segment,
        genesysMA,
        requestTrackingId,
      })

      ssrWindow.open(
        URI.decode(urlWithParams),
        `Popup_Window`,
        `menubar=no,toolbar=no,location=no,directories=no,status=no,scrollbars=no,resizable=no,dependent,width=500,height=${popupHeight},left=0,top=0`,
      )

      const myForm = ssrWindow.document.getElementById(`formID`)
      if (myForm) {
        myForm.target = `Popup_Window`
        myForm.submit()
      }
    } else {
      setHasError(true)
    }
  }

  const handleCallBackClick = () => {
    pushDataWithPageDetails(RIGHT_CHANNELING_CONTACT_BACK)
    // setShowCallBackModal()
    const analyticsData = {
      page: {
        pageURL: ssrWindow.location.pathname,
        pageName: getPagename(language),
      },
    }
    pushToAdobeDataLayer(REQUEST_HELP_CONTACT_BACK, analyticsData)
    !isEmpty(pageLob) && setCustomerLob(pageLob)
    setShowContactBackConfirmation(true)
    const redirect = `${ssrWindow.location.origin}${contactBackUrl}`
    ssrWindow.open(redirect, "_blank") || ssrWindow.location.replace(redirect)
  }

  const handleEmailClick = (mappedSupportForm) => {
    pushDataWithPageDetails(RIGHT_CHANNELING_EMAIL)
    const languageRoute = getLanguageRoute(language)
    const redirect = `${languageRoute}${mappedSupportForm}`

    // forcing refresh for now, using browser routing was causing issues with AppWithModel
    ssrWindow.location.pathname = redirect
  }

  const renderPhoneNumber = () => {
    if (customerContactGuidance) {
      return (
        <Typography data-testid="SupportOptionsGuidance">
          {customerContactGuidance}
        </Typography>
      )
    }

    return (
      <a
        className="wm-link"
        // eslint-disable-next-line react/no-unknown-property
        analytics="contact-back-call-now"
        href={`tel:${customerSupportNumber}`}
        aria-label="click to call this number"
      >
        {customerSupportNumber}
      </a>
    )
  }

  const renderChatHours = () => {
    const weekdayHours = liveChat?.MonFriHrs?.split(`US`)[0]
    const satHours = liveChat?.SatHrs?.split(`US`)[0]

    let nonChatHourMessage = ``
    if (liveChat?.MonFriHrs) {
      if (liveChat?.SatHrs) {
        nonChatHourMessage = `${weekdayHours} and ${satHours}`
      } else {
        nonChatHourMessage = `${weekdayHours}`
      }
    }
    return nonChatHourMessage
  }

  const renderError = () => (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      margin="1rem 0"
      className={classes.messageContainer}
    >
      <ReactSVG
        wrapper="span"
        src={getContentUrl(
          "/content/dam/wm/icons/red-alert-exclamation-circle.svg",
        )}
        className={classes.informationIcon}
        beforeInjection={(svg) => {
          svg.setAttribute("aria-hidden", "true")
        }}
      />

      <Typography variant="body2" component="p">
        {strings.ERROR_MESSAGE}
      </Typography>
    </Box>
  )

  const renderContactBackConfirmation = () => (
    <Box
      data-testid="RequestSupport-options"
      className={classes.contentContainer}
    >
      <Typography variant="h4" component="h4" className={classes.header}>
        {contactOptions?.header}
      </Typography>
      {confirmationIcon && (
        <ReactSVG
          wrapper="span"
          src={getContentUrl(confirmationIcon)}
          style={{ alignSelf: `center` }}
          beforeInjection={(svg) => {
            svg.setAttribute("aria-hidden", "true")
          }}
        />
      )}
      {redirectMessage && (
        <Typography variant="p" component="p" className={classes.body}>
          {redirectMessage}
        </Typography>
      )}
      <ButtonPrimary
        style={{ marginTop: "2.5rem", width: `100%` }}
        onClick={handleCloseModal}
      >
        Close
      </ButtonPrimary>
    </Box>
  )

  const renderTile = ({
    icon,
    header,
    body,
    additionalContent = "",
    buttonText,
    renderButton,
    clickFunction,
    analyticsKey = "",
    testId,
  }) => (
    <Box className={classes.card} data-testid={testId} key={testId}>
      <Box className={classes.cardTopHalf}>
        {icon && (
          <ReactSVG
            wrapper="span"
            src={getContentUrl(icon)}
            className={classes.tileIcon}
            beforeInjection={(svg) => {
              svg.setAttribute("aria-hidden", "true")
            }}
          />
        )}
        {header && (
          <Typography
            variant="h3"
            component="span"
            className={`${classes.tileHeading} maax-font-weight-bold`}
          >
            {header}
          </Typography>
        )}
        {(testId === "SupportOptionsChatDisabled"
          ? additionalContent
          : body) && (
          <Typography
            variant="body1"
            component="span"
            className={classes.tileBody}
          >
            {`${body} `}
          </Typography>
        )}
        {additionalContent && (
          <Typography variant="body1" component="span">
            {additionalContent}
          </Typography>
        )}
      </Box>
      {renderButton && (
        <ButtonPrimary
          onClick={clickFunction}
          className={classes.tileButton}
          analytics_key={analyticsKey}
        >
          {buttonText}
        </ButtonPrimary>
      )}
    </Box>
  )

  const renderContent = () => {
    const isLoadingTiles =
      contactBackAttributesStatus === API_STATUSES.PENDING ||
      liveChat?.fetchState === API_STATUSES.PENDING ||
      phone?.fetchState === API_STATUSES.PENDING

    const tiles = isLoadingTiles ? (
      <Box
        className={classes.card}
        style={{ justifyContent: "center", alignItems: "center" }}
        display="flex"
      >
        <CircularProgress style={{ alignSelf: `center` }} />
      </Box>
    ) : (
      filter(
        map(
          contactOptions?.options,
          ({
            type,
            optionsHeader: header,
            optionsbody: body,
            ctaText,
            icon,
            optionsAlternateHeader,
          }) => {
            let additionalContent = ""

            if (type === CONTACT_METHODS.PHONE) {
              additionalContent = renderPhoneNumber()
              if (
                ((renderTypePhone && isPhoneAllowedByAuthoring) ||
                  renderTypeGuidance) &&
                additionalContent
              ) {
                return renderTile({
                  icon,
                  header,
                  body,
                  additionalContent,
                  buttonText: ctaText,
                  renderButton: !renderTypeGuidance,
                  clickFunction: () => {
                    ssrWindow.open(`tel:${customerSupportNumber}`)
                    pushDataWithPageDetails(RIGHT_CHANNELING_CALL)
                  },
                  analyticsKey: setAnalyticsKey("call"),
                  testId: "SupportOptionsPhone",
                })
              }
              return null
            }

            if (type === CONTACT_METHODS.LIVE_CHAT) {
              additionalContent = renderChatHours()

              // in the case the chatpref API fails, hide the chat option as we won't have the details

              if (isInEditor || renderTypeChat || isChatAllThatsLeft) {
                return renderTile({
                  icon,
                  header: isDuringHours ? header : optionsAlternateHeader,
                  body,
                  additionalContent,
                  buttonText: ctaText,
                  renderButton: isDuringHours,
                  clickFunction: handleChatClick,
                  analyticsKey: setAnalyticsKey("chat"),
                  testId: `SupportOptionsChat${
                    isDuringHours ? "Enabled" : "Disabled"
                  }`,
                })
              }
              return null
            }
            if (type === CONTACT_METHODS.EMAIL) {
              let mappedSupportForm
              if (bypassSupportServlet) {
                mappedSupportForm = false
              } else {
                const { category, subCategory, action } = topSupportTopicData

                const formType = `${category || ""}_${subCategory || ""}${
                  action ? `_${action}` : ""
                }`

                mappedSupportForm =
                  SUPPORT_FORM_TOPIC_MAPPING[
                    toUpper(formType).replace(/\s+/g, "_")
                  ]

                if (!mappedSupportForm && currentPageIsSupportPage) {
                  const languageRoute = getLanguageRoute(language)
                  const articlePath = ssrWindow.location.pathname.replace(
                    languageRoute,
                    "",
                  )

                  mappedSupportForm = SUPPORT_FORM_ARTICLE_MAPPING[articlePath]
                }
              }

              if (isContactBackEligible || (!isInEditor && !mappedSupportForm))
                return null
              // only show email if category/sub category are mapped

              return renderTile({
                icon,
                header,
                body,
                additionalContent: "",
                buttonText: ctaText,
                renderButton: true,
                clickFunction: () => handleEmailClick(mappedSupportForm),
                analyticsKey: setAnalyticsKey("email"),
                testId: "SupportOptionsEmail",
              })
            }
            if (type === CONTACT_METHODS.CALL_BACK) {
              //  only show for the pilot areas
              if (!isInEditor && !renderTypeContactBack) return null

              return renderTile({
                icon,
                header,
                body,
                additionalContent: "",
                buttonText: ctaText,
                renderButton: true,
                clickFunction: handleCallBackClick,
                analyticsKey: setAnalyticsKey("contactBack"),
                testId: "SupportOptionsContactBack",
              })
            }

            return null
          },
        ),
        identity,
      )
    )
    const shouldShowFallback = renderTypeFallback || isEmpty(tiles)
    const unavailableHeader =
      unavailableServiceHeader || contactOptions?.header || ""
    const unavailableText = unavailableServiceText || ""

    const headerContent = (contactOptions?.header || unavailableHeader) && (
      <Typography variant="h4" component="h4" className={classes.header}>
        {
          // get unavailable header, otherwise default to normal so it's not blank
          shouldShowFallback && !isLoadingTiles
            ? unavailableHeader
            : contactOptions?.header
        }
      </Typography>
    )

    const bodyContent = !isLoadingTiles && (
      <FormattedTextField variant="body1" component="p">
        {shouldShowFallback && !isLoadingTiles
          ? unavailableText
          : contactOptions?.body}
      </FormattedTextField>
    )

    return (
      <Box
        data-testid={`RequestSupport-options${
          renderTypeFallback ? `-fallback` : ``
        }`}
        className={classes.contentContainer}
      >
        {headerContent}
        {bodyContent}
        {hasError && renderError()}
        {!shouldShowFallback && <Box className={classes.tiles}>{tiles}</Box>}
      </Box>
    )
  }

  return (
    <>
      {!renderTypeFallback && renderParentHeader()}
      <Box>
        {isContactBackEligible && showContactBackConfirmation
          ? renderContactBackConfirmation()
          : renderContent()}
      </Box>
    </>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// SupportOptionsCards.propTypes = {
//   strings: PropTypes.object.isRequired,
//   contactOptions: PropTypes.shape({
//     header: PropTypes.string,
//     body: PropTypes.string,
//     unavailableServiceHeader: PropTypes.string,
//     unavailableServiceText: PropTypes.string,
//     backButtonText: PropTypes.string,
//     options: PropTypes.arrayOf(
//       PropTypes.shape({
//         type: PropTypes.string, // options of "email", "phone", "callBack", "liveChat"
//         header: PropTypes.string,
//         body: PropTypes.string,
//       }),
//     ),
//   }).isRequired,
//   header: PropTypes.string,
//   body: PropTypes.string,
//   unavailableServiceHeader: PropTypes.string,
//   unavailableServiceText: PropTypes.string,
//   topSupportTopicData: PropTypes.object.isRequired,
//   customerSelections: PropTypes.object.isRequired,
//   customerSupportContact: PropTypes.object.isRequired,
//   language: PropTypes.string.isRequired,
//   isLoggedIn: PropTypes.bool.isRequired,
//   getLiveChat: PropTypes.func.isRequired,
//   contactBackUrl: PropTypes.string.isRequired,
//   confirmationIcon: PropTypes.string,
//   redirectMessage: PropTypes.string,
//   hidePhoneForBusinessLOB: PropTypes.bool.isRequired,
//   hidePhoneForHomeLOB: PropTypes.bool.isRequired,
//   handleCloseModal: PropTypes.func.isRequired,
//   hasError: PropTypes.bool.isRequired,
//   setHasError: PropTypes.func.isRequired,
//   cityBilledAnalytics: PropTypes.bool,
//   cityBilledAnalyticsData: PropTypes.object,
//   isLobHome: PropTypes.bool.isRequired,
//   isLobBusiness: PropTypes.bool.isRequired,
//   bypassSupportServlet: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// SupportOptionsCards.defaultProps = {
//   unavailableServiceHeader: "",
//   unavailableServiceText: "",
//   confirmationIcon: "",
//   redirectMessage: "",
//   bypassSupportServlet: false,
//   cityBilledAnalytics: false,
//   cityBilledAnalyticsData: {},
// }

export default withLocalization(strings)(SupportOptionsCards)
